import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import ColorPickerModal from './ColorPickerModal';
import CreateColorPresetModal from './CreateColorPresetModal';
import { getColorPresets, createColorPreset, deleteColorPreset, ColorPreset } from '../lib/color-presets';

interface BookingColorPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onColorSelect?: (color: string, secondaryColor?: string) => void;
  currentColor?: string;
  currentSecondaryColor?: string;
  businessId?: string;
}

export default function BookingColorPickerModal({
  isOpen,
  onClose,
  onColorSelect,
  currentColor = '#3b82f6',
  currentSecondaryColor,
  businessId
}: BookingColorPickerModalProps) {
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [isCreateColorPresetModalOpen, setIsCreateColorPresetModalOpen] = useState(false);
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [selectedColorMethod, setSelectedColorMethod] = useState<'custom' | string>('custom');
  const [bookingColor, setBookingColor] = useState(currentColor);
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState(currentSecondaryColor || null);

  // Load color presets when modal opens
  useEffect(() => {
    if (isOpen && businessId) {
      loadColorPresets();
    }
  }, [isOpen, businessId]);

  // Update colors when props change
  useEffect(() => {
    setBookingColor(currentColor);
    setBookingSecondaryColor(currentSecondaryColor || null);
  }, [currentColor, currentSecondaryColor]);

  const loadColorPresets = async () => {
    if (!businessId) return;
    
    try {
      const presets = await getColorPresets(businessId);
      setColorPresets(presets);
    } catch (error) {
      console.error('Error loading color presets:', error);
    }
  };

  if (!isOpen) return null;

  const handleSave = () => {
    if (onColorSelect) {
      onColorSelect(bookingColor, bookingSecondaryColor || undefined);
    }
    onClose();
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Booking Colour</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center flex-wrap gap-3">
                {/* Current Colour Button */}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedColorMethod('custom');
                    setIsColorPickerModalOpen(true);
                  }}
                  className={`flex flex-col items-center space-y-2 px-4 py-3 border-2 rounded-md hover:bg-gray-50 focus:outline-none transition-colors ${
                    selectedColorMethod === 'custom' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                >
                  <span className="text-sm text-gray-700">Current Colour</span>
                  <div className="w-10 h-10 rounded-full border border-gray-300 overflow-hidden">
                    {bookingSecondaryColor ? (
                      <div className="w-full h-full flex">
                        <div 
                          className="w-1/2 h-full"
                          style={{ backgroundColor: bookingColor }}
                        />
                        <div 
                          className="w-1/2 h-full"
                          style={{ backgroundColor: bookingSecondaryColor }}
                        />
                      </div>
                    ) : (
                      <div 
                        className="w-full h-full"
                        style={{ backgroundColor: bookingColor }}
                      />
                    )}
                  </div>
                </button>

                {/* Color Presets */}
                {colorPresets.map((preset) => (
                  <div key={preset.id} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedColorMethod(preset.id);
                        setBookingColor(preset.primary_color);
                        setBookingSecondaryColor(preset.secondary_color || null);
                      }}
                      className={`flex flex-col items-center space-y-2 px-4 py-3 border-2 rounded-md hover:bg-gray-50 focus:outline-none transition-colors ${
                        selectedColorMethod === preset.id 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300'
                      }`}
                    >
                      <span className="text-sm text-gray-700">{preset.title}</span>
                      <div className="w-10 h-10 rounded-full border border-gray-300 overflow-hidden">
                        {preset.secondary_color ? (
                          <div className="w-full h-full flex">
                            <div 
                              className="w-1/2 h-full"
                              style={{ backgroundColor: preset.primary_color }}
                            />
                            <div 
                              className="w-1/2 h-full"
                              style={{ backgroundColor: preset.secondary_color }}
                            />
                          </div>
                        ) : (
                          <div 
                            className="w-full h-full"
                            style={{ backgroundColor: preset.primary_color }}
                          />
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        
                        // Show confirmation dialog
                        const confirmed = window.confirm(
                          `Are you sure you want to delete the "${preset.title}" color preset? This action cannot be undone.`
                        );
                        
                        if (!confirmed) return;
                        
                        try {
                          await deleteColorPreset(preset.id);
                          setColorPresets(prev => prev.filter(p => p.id !== preset.id));
                          if (selectedColorMethod === preset.id) {
                            setSelectedColorMethod('custom');
                          }
                        } catch (error) {
                          console.error('Error deleting color preset:', error);
                        }
                      }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                    >
                      ×
                    </button>
                  </div>
                ))}

                {/* Add Preset Button */}
                <button
                  type="button"
                  onClick={() => setIsCreateColorPresetModalOpen(true)}
                  className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Save/Cancel Buttons */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
              >
                Save Color
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Color Picker Modal */}
      <ColorPickerModal
        isOpen={isColorPickerModalOpen}
        onClose={() => setIsColorPickerModalOpen(false)}
        onColorSelect={(color, secondaryColor) => {
          setSelectedColorMethod('custom');
          setBookingColor(color);
          setBookingSecondaryColor(secondaryColor || null);
          setIsColorPickerModalOpen(false);
        }}
        currentColor={bookingColor}
        currentSecondaryColor={bookingSecondaryColor || undefined}
      />

      {/* Create Color Preset Modal */}
      <CreateColorPresetModal
        isOpen={isCreateColorPresetModalOpen}
        onClose={() => setIsCreateColorPresetModalOpen(false)}
        onSave={async (title, primaryColor, secondaryColor) => {
          if (!businessId) return;
          
          try {
            const newPreset = await createColorPreset({
              business_id: businessId,
              title,
              primary_color: primaryColor,
              secondary_color: secondaryColor
            });
            setColorPresets(prev => [...prev, newPreset]);
            setIsCreateColorPresetModalOpen(false);
          } catch (error) {
            console.error('Error creating color preset:', error);
          }
        }}
      />
    </>
  );
} 